import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Button from 'antd/lib/button';
import Menu from 'antd/lib/menu';
import Text from 'antd/lib/typography/Text';

import { antGrey, runwayColors } from './styles/globalStyles';
import { AppComponentProps } from '../typescriptHelpers';
import { createSettingsMenuRoute } from '../helpers/routeHelpers';
import { userIsRunwayAdmin } from '../helpers';
import Space from 'antd/lib/space';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { selectAppSettings, selectWorkflowEnabledSteps } from '../selectors/selectors';
import { getConnectionErrorIntegration, getInvalidIntegration } from '../helpers/integrationHelpers';
import { getSeenNewAutomations } from '../helpers/localStorageHelper';
import Icon, { WarningFilled } from '@ant-design/icons';
import { settingsMenuItems } from './appSettings/appSettingsHelpers';
import { useApp } from '@/utils/hooks/helpers';
import { PlatformsLabel } from './sharedComponents/PlatformsLabel';

interface SettingsNavProps extends AppComponentProps {
    isCollapsed: boolean;
}

const SettingsNav: React.FC<SettingsNavProps> = ({ appId, orgId, isCollapsed }) => {
    const {
        appSettings: { platform, appStoreType },
    } = useApp({ appId, orgId });

    const appSettings = useSelector((state: RootState) => selectAppSettings(state, appId));

    const seenNewAutomations = getSeenNewAutomations();

    const hasNewAutomation = appSettings.automations.some(
        (automation) => automation.isNew && !seenNewAutomations?.[automation.type],
    );

    const { pathname } = useLocation();

    const needsTag = getInvalidIntegration(appSettings) || getConnectionErrorIntegration(appSettings);

    const isRunwayAdmin = useSelector((state: RootState) => userIsRunwayAdmin(state.user));

    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));

    const menuItems = settingsMenuItems.filter((item) => {
        if (item.minimumRequiredMigratedWorkflowStepsToShow) {
            return item.minimumRequiredMigratedWorkflowStepsToShow.every((step) => workflowEnabledSteps[step]);
        }

        if (item.minimumRequiredMigratedWorkflowStepsToHide) {
            return !item.minimumRequiredMigratedWorkflowStepsToHide.some((step) => workflowEnabledSteps[step]);
        }

        if (item.isRunwayAdminRouteOnly) {
            return isRunwayAdmin;
        }

        return true;
    });

    return (
        <>
            <Space direction="vertical" size={24}>
                <div style={{ display: 'flex', padding: '10px 16px', justifyContent: 'flex-end' }}>
                    <PlatformsLabel appId={appId} platform={platform} appStoreType={appStoreType} />
                </div>
                <Menu
                    style={{ backgroundColor: antGrey.grey3, borderRight: 'none' }}
                    selectedKeys={[pathname]}
                    className="settings-sidenav"
                >
                    {menuItems.map((item) => {
                        const route = createSettingsMenuRoute(orgId, appId, item.path);
                        let isActive = pathname === route;
                        let subroute;
                        //if this menu item route doesn't match the current path, try its subroute
                        if (!isActive && item.subroute) {
                            subroute = item.subroute(orgId, appId);
                        }
                        if (subroute === pathname) {
                            isActive = true;
                        }
                        const hideSetting =
                            item.showOnlyOnPlatforms ? !item.showOnlyOnPlatforms.includes(platform) : false;

                        return (
                            !hideSetting && (
                                <Menu.Item
                                    key={subroute && isActive ? subroute : route}
                                    style={{
                                        backgroundColor: isActive && antGrey.grey5,
                                        padding: isCollapsed ? '0px' : '0px 3px 0px 25px',
                                    }}
                                    title="" //hides tooltip on collapse
                                >
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                        {needsTag && item.id === 'integrations' && (
                                            <WarningFilled
                                                style={{
                                                    color: runwayColors.statusYellow,
                                                    flexGrow: 1,
                                                    display: 'flex',
                                                }}
                                            />
                                        )}
                                        <NavLink to={route} activeStyle={{ color: runwayColors.black }}>
                                            {hasNewAutomation && item.id === 'automations' && (
                                                <div
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        display: 'inline-block',
                                                        backgroundColor: runwayColors.primary,
                                                        borderRadius: 20,
                                                        marginRight: 14,
                                                    }}
                                                />
                                            )}
                                            <Text>{item.label}</Text>
                                            <Button type="link">
                                                <Icon
                                                    component={item.icon}
                                                    style={{
                                                        color: isActive ? antGrey.grey1 : antGrey.grey5,
                                                    }}
                                                />
                                            </Button>
                                        </NavLink>
                                    </div>
                                </Menu.Item>
                            )
                        );
                    })}
                </Menu>
            </Space>
        </>
    );
};

export default SettingsNav;
